import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../../../assets/contexts/ThemeContext';

/**
 * 영역별 컨테이너 너비 제어 컴포넌트
 * 원본: area-container-width-item.html
 */
const AreaContainerWidthControl = ({ areaKey, areaName, areaSelector, areaNumber }) => {
  const { themeConfig, set } = useTheme();
  const currentThemeConfig = themeConfig || {};

  // 기본값 정의 (원본과 동일) - useMemo로 메모이제이션
  const defaultContainerWidths = useMemo(() => ({ xl: 1400, lg: 992, md: 768, sm: 576 }), []);

  // 영역별 CSS 변수 이름 맵 (원본과 동일) - useMemo로 메모이제이션
  const areaVarNameMap = useMemo(() => ({
    'header-top': '--container-max-width-header-top',
    'header-bottom': '--container-max-width-header-bottom',
    'banner': '--container-max-width-banner',
    'content': '--container-max-width-content',
    'footer': '--container-max-width-footer',
    'related-sites': '--container-max-width-related-sites'
  }), []);

  // 각 사이즈별 state 관리
  const [widths, setWidths] = useState({ xl: defaultContainerWidths.xl, lg: defaultContainerWidths.lg, md: defaultContainerWidths.md, sm: defaultContainerWidths.sm });
  const isInitializedRef = useRef(false);

  // 현재 영역의 컨테이너 너비 가져오기 (원본 getCurrentAreaContainerWidths 로직과 동일)
  const getCurrentAreaContainerWidths = useCallback(() => {
    const widths = {};
    const varPrefix = areaVarNameMap[areaKey] || `--container-max-width-${areaKey}`;

    try {
      const computedStyle = window.getComputedStyle(document.documentElement);
      ['xl', 'lg', 'md', 'sm'].forEach(size => {
        const varName = `${varPrefix}-${size}`;
        const value = computedStyle.getPropertyValue(varName).trim();

        if (value) {
          const match = value.match(/^([\d.]+)/);
          if (match) {
            if (value.indexOf('var(') === -1) {
              widths[size] = parseInt(match[1], 10);
            } else {
              // var() 참조인 경우 전역 변수에서 가져오기
              const globalVarName = `--container-max-width-${size}`;
              const globalValue = computedStyle.getPropertyValue(globalVarName).trim();
              const globalMatch = globalValue.match(/^([\d.]+)/);
              widths[size] = globalMatch ? parseInt(globalMatch[1], 10) : defaultContainerWidths[size];
            }
          } else {
            widths[size] = defaultContainerWidths[size];
          }
        } else {
          // CSS 변수가 없으면 기본값 사용
          widths[size] = defaultContainerWidths[size];
        }
      });
    } catch (e) {
      console.warn('영역별 컨테이너 너비 CSS 변수를 읽는 중 오류:', e);
      return defaultContainerWidths;
    }

    return widths;
  }, [areaKey, areaVarNameMap, defaultContainerWidths]);

  // 초기화: 저장된 값 또는 CSS 변수에서 읽어오기
  useEffect(() => {
    if (isInitializedRef.current) return;

    let initialWidths = { ...defaultContainerWidths };

    // 저장된 값이 있으면 사용
    const savedWidths = currentThemeConfig.areaContainerWidths?.[areaKey];
    if (savedWidths) {
      ['xl', 'lg', 'md', 'sm'].forEach(size => {
        if (savedWidths[size] !== undefined && savedWidths[size] !== null) {
          initialWidths[size] = savedWidths[size];
        } else {
          const cssWidths = getCurrentAreaContainerWidths();
          initialWidths[size] = cssWidths[size] || defaultContainerWidths[size];
        }
      });
    } else {
      // CSS 변수에서 읽어오기
      initialWidths = getCurrentAreaContainerWidths();
    }

    setWidths(initialWidths);
    isInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시에만 실행 (의도적으로 의존성 배열 비움)

  // 영역별 컨테이너 너비 업데이트 (원본 updateAreaContainerWidth 로직과 동일)
  const updateAreaContainerWidth = (size, widthValue) => {
    try {
      const numValue = parseInt(widthValue, 10);
      if (isNaN(numValue) || numValue < 0 || numValue > 5000) {
        return;
      }

      // State 업데이트
      setWidths(prev => ({ ...prev, [size]: numValue }));

      // ThemeConfig에 저장
      set(`areaContainerWidths.${areaKey}.${size}`, numValue);

      // CSS 변수 업데이트
      const varPrefix = areaVarNameMap[areaKey] || `--container-max-width-${areaKey}`;
      const varName = `${varPrefix}-${size}`;
      const widthPx = `${numValue}px`;
      document.documentElement.style.setProperty(varName, widthPx);
    } catch (e) {
      console.error('영역별 컨테이너 너비 업데이트 중 오류:', e);
    }
  };

  return (
    <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee', marginBottom: '15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#555', fontSize: '12px', marginBottom: '8px' }}>
        <span>{areaNumber}. {areaName} ({areaSelector})</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
        {['xl', 'lg', 'md', 'sm'].map(size => {
          const currentValue = widths[size] || defaultContainerWidths[size];
          return (
            <div key={size} style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '10px', color: '#666', marginBottom: '3px' }}>{size.toUpperCase()}:</label>
              <input
                type="number"
                value={currentValue}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 0 && value <= 5000) {
                    updateAreaContainerWidth(size, value);
                  }
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (isNaN(value) || value < 0 || value > 5000) {
                    // 유효하지 않은 값이면 현재 유효한 값으로 복원
                    updateAreaContainerWidth(size, currentValue);
                  }
                }}
                step="1"
                min="0"
                max="5000"
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  paddingRight: '30px',
                  border: '1px solid #ddd',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  textAlign: 'center'
                }}
              />
              <div style={{ position: 'absolute', right: '6px', bottom: '5px', fontSize: '10px', color: '#999', pointerEvents: 'none' }}>px</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AreaContainerWidthControl;

